/**
 * Window geometry on the web canvas. Pure functions only: everything here
 * takes the canvas size as an argument, so restoring a session on a smaller
 * screen, snapping and tidying can all be tested without a browser.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Size {
  w: number;
  h: number;
}

/**
 * Where a window is docked, if anywhere. `null` means it floats freely.
 * Snapping reaches the first seven; the layout surface reaches all of them.
 */
export type Zone = 'max' | 'left' | 'right' | 'tl' | 'tr' | 'bl' | 'br' | 'top' | 'bottom' | 'third1' | 'third2' | 'third3' | 'almost';

export const ZONES: readonly Zone[] = ['max', 'left', 'right', 'tl', 'tr', 'bl', 'br', 'top', 'bottom', 'third1', 'third2', 'third3', 'almost'];

export const MAX_WINDOWS = 12;
export const MIN_SIZE: Size = { w: 360, h: 300 };
/** Breathing room between a docked window and the canvas edge. */
export const EDGE_GAP = 12;
/** Space between windows when they are arranged. */
export const TILE_GAP = 12;

/** Writing windows are a little taller than wide, like a page. */
export const PAGE_RATIO = 0.78;

export function sameRect(a: Rect | null | undefined, b: Rect | null | undefined): boolean {
  if (!a || !b) return a === b;
  return Math.round(a.x) === Math.round(b.x) && Math.round(a.y) === Math.round(b.y) && Math.round(a.w) === Math.round(b.w) && Math.round(a.h) === Math.round(b.h);
}

const round = (r: Rect): Rect => ({ x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) });

/** Margins around the first window: generous on roomy screens, tight on small ones. */
function margins(canvas: Size) {
  return {
    x: Math.round(Math.min(120, Math.max(16, canvas.w * 0.07))),
    y: Math.round(Math.min(72, Math.max(16, canvas.h * 0.075))),
  };
}

/**
 * The first window: centred, page-like, never filling the screen.
 * It is as tall as the canvas comfortably allows and about 0.78 as wide.
 */
export function defaultRect(canvas: Size): Rect {
  const m = margins(canvas);
  const h = Math.min(980, canvas.h - 2 * m.y);
  const w = Math.min(canvas.w - 2 * m.x, Math.max(520, Math.min(700, h * PAGE_RATIO)));
  return clampRect(round({ x: (canvas.w - w) / 2, y: (canvas.h - h) / 2, w, h }), canvas);
}

/** The largest size a floating window may take on this canvas. */
export function maxSize(canvas: Size): Size {
  return { w: Math.max(1, canvas.w - 2 * EDGE_GAP), h: Math.max(1, canvas.h - 2 * EDGE_GAP) };
}

/**
 * Keep a window usable: no smaller than the minimum (when the canvas allows),
 * no larger than the canvas, and entirely on screen. A window can never be
 * restored somewhere it cannot be reached.
 */
export function clampRect(rect: Rect, canvas: Size): Rect {
  const max = maxSize(canvas);
  const w = Math.min(max.w, Math.max(Math.min(MIN_SIZE.w, max.w), rect.w));
  const h = Math.min(max.h, Math.max(Math.min(MIN_SIZE.h, max.h), rect.h));
  const x = Math.min(canvas.w - EDGE_GAP - w, Math.max(EDGE_GAP, rect.x));
  const y = Math.min(canvas.h - EDGE_GAP - h, Math.max(EDGE_GAP, rect.y));
  return round({ x: Math.max(0, x), y: Math.max(0, y), w, h });
}

/**
 * While dragging, a window may hang partly off the canvas — but a strip of
 * its head always stays reachable.
 */
export function clampDrag(rect: Rect, canvas: Size, keep = 96): Rect {
  return {
    ...rect,
    x: Math.min(canvas.w - keep, Math.max(keep - rect.w, rect.x)),
    y: Math.min(canvas.h - 48, Math.max(0, rect.y)),
  };
}

export function zoneRect(zone: Zone, canvas: Size): Rect {
  const g = EDGE_GAP;
  const fullW = canvas.w - 2 * g;
  const fullH = canvas.h - 2 * g;
  const halfW = (fullW - TILE_GAP) / 2;
  const halfH = (fullH - TILE_GAP) / 2;
  const right = g + halfW + TILE_GAP;
  const lower = g + halfH + TILE_GAP;
  const thirdW = (fullW - 2 * TILE_GAP) / 3;
  // Almost maximised: nine tenths of the canvas, centred — room to see the canvas around it.
  const almostW = Math.max(fullW * 0.9, Math.min(fullW, MIN_SIZE.w));
  const almostH = Math.max(fullH * 0.9, Math.min(fullH, MIN_SIZE.h));
  const rects: Record<Zone, Rect> = {
    top: { x: g, y: g, w: fullW, h: halfH },
    bottom: { x: g, y: lower, w: fullW, h: halfH },
    third1: { x: g, y: g, w: thirdW, h: fullH },
    third2: { x: g + thirdW + TILE_GAP, y: g, w: thirdW, h: fullH },
    third3: { x: g + 2 * (thirdW + TILE_GAP), y: g, w: thirdW, h: fullH },
    almost: { x: (canvas.w - almostW) / 2, y: (canvas.h - almostH) / 2, w: almostW, h: almostH },
    max: { x: g, y: g, w: fullW, h: fullH },
    left: { x: g, y: g, w: halfW, h: fullH },
    right: { x: right, y: g, w: halfW, h: fullH },
    tl: { x: g, y: g, w: halfW, h: halfH },
    tr: { x: right, y: g, w: halfW, h: halfH },
    bl: { x: g, y: lower, w: halfW, h: halfH },
    br: { x: right, y: lower, w: halfW, h: halfH },
  };
  return round(rects[zone]);
}

/**
 * The snap zone under the pointer while a window is dragged: the top edge
 * maximises, the side edges take a half, the corners a quarter. `reach` is
 * how close to an edge counts as touching it.
 */
export function zoneAt(point: { x: number; y: number }, canvas: Size, reach = 14): Zone | null {
  const left = point.x <= reach;
  const right = point.x >= canvas.w - reach;
  const top = point.y <= reach;
  const bottom = point.y >= canvas.h - reach;
  const cornerY = canvas.h * 0.22;
  if (left || right) {
    if (point.y <= cornerY) return left ? 'tl' : 'tr';
    if (point.y >= canvas.h - cornerY) return left ? 'bl' : 'br';
    return left ? 'left' : 'right';
  }
  if (top) return 'max';
  if (bottom) return null;
  return null;
}

function overlap(a: Rect, b: Rect): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * Where a new window goes. If there is free room beside the window it was
 * opened from, it takes that room; otherwise it cascades down and to the
 * right, the way stacked paper is offset so every sheet stays visible.
 */
export function placeNew(from: Rect | null, others: readonly Rect[], canvas: Size): Rect {
  const base = from ?? defaultRect(canvas);
  const size = { w: base.w, h: base.h };
  const free = (r: Rect) => r.x >= EDGE_GAP && r.y >= EDGE_GAP && r.x + r.w <= canvas.w - EDGE_GAP && r.y + r.h <= canvas.h - EDGE_GAP && others.every((o) => overlap(o, r) < 0.04 * r.w * r.h);
  const gap = 20;
  const candidates: Rect[] = [
    { ...size, x: base.x + base.w + gap, y: base.y },
    { ...size, x: base.x - size.w - gap, y: base.y },
  ];
  for (const c of candidates) if (free(c)) return round(c);

  const step = 28;
  let rect = { ...size, x: base.x + step, y: base.y + step };
  for (let i = 0; i < 24; i++) {
    const clash = others.some((o) => Math.abs(o.x - rect.x) < 6 && Math.abs(o.y - rect.y) < 6);
    const fits = rect.x + rect.w <= canvas.w - EDGE_GAP && rect.y + rect.h <= canvas.h - EDGE_GAP;
    if (!fits) rect = { ...rect, x: EDGE_GAP + ((i * step) % 160), y: EDGE_GAP + ((i * step) % 120) };
    else if (!clash) break;
    else rect = { ...rect, x: rect.x + step, y: rect.y + step };
  }
  return clampRect(rect, canvas);
}

/** Resize from an edge or corner. `dir` holds -1, 0 or 1 per axis (e.g. bottom-right is {x: 1, y: 1}). */
export function resizeRect(start: Rect, dir: { x: -1 | 0 | 1; y: -1 | 0 | 1 }, dx: number, dy: number, canvas: Size): Rect {
  const max = maxSize(canvas);
  const minW = Math.min(MIN_SIZE.w, max.w);
  const minH = Math.min(MIN_SIZE.h, max.h);
  let { x, y, w, h } = start;
  if (dir.x === 1) w = Math.min(canvas.w - EDGE_GAP / 2 - x, Math.max(minW, start.w + dx));
  if (dir.x === -1) {
    const right = start.x + start.w;
    w = Math.min(right - EDGE_GAP / 2, Math.max(minW, start.w - dx));
    x = right - w;
  }
  if (dir.y === 1) h = Math.min(canvas.h - EDGE_GAP / 2 - y, Math.max(minH, start.h + dy));
  if (dir.y === -1) {
    const bottom = start.y + start.h;
    h = Math.min(bottom - EDGE_GAP / 2, Math.max(minH, start.h - dy));
    y = bottom - h;
  }
  return round({ x, y, w, h });
}

/**
 * Fit a restored window into the current canvas. Docked windows are
 * re-docked; floating windows keep their size where possible and are moved
 * fully on screen. Coordinates from a larger screen are scaled down first so
 * a layout keeps its rough shape instead of piling up in a corner.
 */
export function normalizeRect(rect: Rect, zone: Zone | null, canvas: Size, savedCanvas?: Size): Rect {
  if (zone) return zoneRect(zone, canvas);
  let r = rect;
  if (savedCanvas && (savedCanvas.w > canvas.w || savedCanvas.h > canvas.h)) {
    const sx = Math.min(1, canvas.w / savedCanvas.w);
    const sy = Math.min(1, canvas.h / savedCanvas.h);
    r = { x: r.x * sx, y: r.y * sy, w: Math.max(r.w * Math.min(sx, sy), Math.min(r.w, canvas.w - 2 * EDGE_GAP)), h: r.h };
  }
  if (!Number.isFinite(r.x + r.y + r.w + r.h)) return defaultRect(canvas);
  return clampRect(r, canvas);
}
